import { useMemo, useState } from 'react';
import { Button, Chip, Input, toast } from '@heroui/react';
import { Sheet } from '@heroui-pro/react/sheet';
import { Segment } from '@heroui-pro/react/segment';
import { ChevronDown, FileSpreadsheet, FileText } from 'lucide-react';
import { isTopSegment, type Light, type SegmentFilter, type TreasuryRow } from '@odd/domain';
import { useTreasury } from '../lib/queries';
import { useErpLinks } from '../lib/erp';
import { makeMatcher } from '../lib/search';
import { fmtSnapshot } from '../lib/format';
import { useBoardCtx } from './BoardContext';
import { ErpLink, SegmentBadge } from './ui';
import { TreasuryGuide } from './TreasuryGuide';
import { TreasurySummary } from './TreasurySummary';
import { TreasuryTable, type TreasurySortKey } from './TreasuryTable';
import './TreasurySheet.css';

const RANK: Record<Light, number> = { rojo: 0, ambar: 1, verde: 2 };

/** Peor semáforo de la comunidad (para ordenar por gravedad) */
const worstLight = (r: TreasuryRow) => Math.min(RANK[r.f1Light ?? 'verde'], RANK[r.f2Light ?? 'verde']);
const outOfThreshold = (r: TreasuryRow) =>
  (r.f1Light && r.f1Light !== 'verde') || (r.f2Light && r.f2Light !== 'verde');

/** Detalle de tesorería y deuda de la oficina (KPI «Tesorería, deuda y altas»): saldos de Gesfincas de la foto */
export function TreasurySheet({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { board, officeId, filter } = useBoardCtx();
  const runId = board.isCurrent ? null : (board.snapshot?.runId ?? null);
  const q = useTreasury(officeId, runId, isOpen);
  const erp = useErpLinks();

  const [segment, setSegment] = useState<SegmentFilter>(filter.segment);
  const [view, setView] = useState<'out' | 'all'>('out');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: TreasurySortKey; dir: 1 | -1 }>({ key: 'worst', dir: 1 });
  const [showMissing, setShowMissing] = useState(false);

  const report = q.data;
  const bySegment = useMemo(
    () => (report?.rows ?? []).filter((r) => segment === 'ALL' || isTopSegment(r.segment)),
    [report, segment],
  );
  const rows = useMemo(() => {
    const match = makeMatcher(search);
    const list = bySegment.filter(
      (r) => (view === 'all' || outOfThreshold(r)) && match(r.communityName, r.code, r.affName),
    );
    const val = (r: TreasuryRow): number | string =>
      sort.key === 'worst'
        ? worstLight(r) * 1e6 + (r.f1 ?? 1e5)
        : sort.key === 'name'
          ? r.communityName
          : (r[sort.key] ?? Infinity);
    return [...list].sort((a, b) => {
      const x = val(a),
        y = val(b);
      return (typeof x === 'string' ? x.localeCompare(String(y), 'es') : x - (y as number)) * sort.dir;
    });
  }, [bySegment, view, search, sort]);

  const s = useMemo(() => {
    const c = (k: 'f1Light' | 'f2Light', l: Light) => bySegment.filter((r) => r[k] === l).length;
    const sum = (k: 'bancos' | 'caja' | 'impagados' | 'gastos12m' | 'deudaProveedores') =>
      bySegment.reduce((t, r) => t + r[k], 0);
    return {
      f1r: c('f1Light', 'rojo'),
      f1a: c('f1Light', 'ambar'),
      f2r: c('f2Light', 'rojo'),
      f2a: c('f2Light', 'ambar'),
      negative: bySegment.filter((r) => r.disponible < 0).length,
      out: bySegment.filter(outOfThreshold).length,
      liquidez: sum('bancos') + sum('caja'),
      deuda: sum('deudaProveedores'),
      impagados: sum('impagados'),
      gastos: sum('gastos12m'),
    };
  }, [bySegment]);
  const missing = (report?.missing ?? []).filter((m) => segment === 'ALL' || isTopSegment(m.segment));

  const [exporting, setExporting] = useState(false);
  /** Exporta lo que se ve: filas filtradas y ordenadas, con el resumen del segmento */
  const exportAs = async (kind: 'csv' | 'pdf') => {
    if (!report) return;
    const filterLabel = [
      segment === 'AB' ? 'segmento A + B' : 'todos los segmentos',
      view === 'out' ? 'fuera de umbral' : 'todas las comunidades',
      search.trim() ? `búsqueda «${search.trim()}»` : null,
    ]
      .filter(Boolean)
      .join(' · ');
    const payload = {
      officeName: board.office.name,
      report,
      rows,
      filterLabel,
      summary: {
        out: s.out,
        total: bySegment.length,
        missing: missing.length,
        coberturaRojo: s.f1r,
        coberturaAmbar: s.f1a,
        negativo: s.negative,
        morosidadRojo: s.f2r,
        morosidadAmbar: s.f2a,
        liquidez: s.liquidez,
        deudaProveedores: s.deuda,
        impagados: s.impagados,
        gastos: s.gastos,
      },
    };
    try {
      setExporting(kind === 'pdf');
      const mod = await import('../lib/treasuryExport');
      if (kind === 'csv') mod.treasuryCsv(payload);
      else await mod.treasuryPdf(payload);
    } catch (e) {
      toast.danger(`No se pudo generar el ${kind.toUpperCase()}: ${(e as Error).message}`);
    } finally {
      setExporting(false);
    }
  };

  const sortBy = (key: TreasurySortKey) =>
    setSort((cur) =>
      cur.key === key ? { key, dir: cur.dir === 1 ? -1 : 1 } : { key, dir: key === 'name' ? 1 : 1 },
    );

  return (
    <Sheet isOpen={isOpen} onOpenChange={(o) => !o && onClose()} placement="right">
      <Sheet.Backdrop>
        <Sheet.Content className="treasury-sheet">
          <Sheet.Dialog className="treasury-dialog">
            <Sheet.CloseTrigger />
            <Sheet.Header className="treasury-header">
              <div className="text-xs font-bold tracking-wider text-orange-ink uppercase">
                Finanzas operativas · cobertura de caja y morosidad
              </div>
              <Sheet.Heading className="text-2xl font-extrabold text-ink">
                Tesorería y deuda · {board.office.name}
              </Sheet.Heading>
              <p className="text-sm text-ink-3">
                {report?.loadedAt
                  ? `Saldos de Gesfincas cargados el ${fmtSnapshot(report.loadedAt)} · foto del ${fmtSnapshot(report.snapshotTakenAt!)}`
                  : q.isLoading
                    ? 'Cargando…'
                    : 'Sin saldos de Gesfincas en esta foto'}
              </p>
            </Sheet.Header>
            <Sheet.Body className="treasury-body space-y-5">
              <TreasurySummary
                summary={s}
                total={bySegment.length}
                missing={missing.length}
                segment={segment}
                thresholds={report?.thresholds}
                isLoading={q.isLoading}
              />

              <TreasuryGuide thresholds={report?.thresholds} />

              <div className="flex flex-wrap items-center gap-2">
                <Segment
                  size="sm"
                  aria-label="Segmento"
                  selectedKey={segment}
                  onSelectionChange={(k) => setSegment(k as SegmentFilter)}
                >
                  <Segment.Item id="AB">A + B</Segment.Item>
                  <Segment.Item id="ALL">Todos</Segment.Item>
                </Segment>
                <Segment
                  size="sm"
                  aria-label="Qué comunidades"
                  selectedKey={view}
                  onSelectionChange={(k) => setView(k as 'out' | 'all')}
                >
                  <Segment.Item id="out">Fuera de umbral</Segment.Item>
                  <Segment.Item id="all">Todas</Segment.Item>
                </Segment>
                <Input
                  aria-label="Buscar comunidad"
                  placeholder="Buscar comunidad, código o AFF"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="min-w-0 flex-1 basis-56"
                />
                <div className="ml-auto flex shrink-0 gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    isDisabled={!report || rows.length === 0}
                    onPress={() => exportAs('csv')}
                  >
                    <FileSpreadsheet className="size-4" /> CSV
                  </Button>
                  <Button
                    size="sm"
                    isDisabled={!report || rows.length === 0}
                    isPending={exporting}
                    onPress={() => exportAs('pdf')}
                  >
                    <FileText className="size-4" /> Informe PDF
                  </Button>
                </div>
              </div>

              <TreasuryTable rows={rows} sort={sort} onSort={sortBy} isLoading={q.isLoading} view={view} />

              {missing.length > 0 && (
                <div className="rounded-2xl border border-dashed border-line p-4">
                  <button
                    type="button"
                    onClick={() => setShowMissing((v) => !v)}
                    className="flex items-center gap-2 text-sm font-semibold text-ink"
                  >
                    <ChevronDown
                      className={`size-4 transition-transform ${showMissing ? 'rotate-180' : ''}`}
                    />
                    {missing.length} {missing.length === 1 ? 'comunidad sin saldo' : 'comunidades sin saldo'}{' '}
                    en Gesfincas
                  </button>
                  {showMissing && (
                    <ul className="mt-3 grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
                      {missing.map((m) => (
                        <li key={m.id} className="flex items-center gap-2 text-sm">
                          <SegmentBadge segment={m.segment} />
                          <ErpLink href={erp.community(m.id)} title="Abrir la comunidad en Eleva">
                            {m.name}
                          </ErpLink>
                          <Chip size="sm" variant="soft">
                            <Chip.Label>
                              {m.hasCode ? 'sin fila en Gesfincas' : 'sin «Código» en el ERP'}
                            </Chip.Label>
                          </Chip>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </Sheet.Body>
          </Sheet.Dialog>
        </Sheet.Content>
      </Sheet.Backdrop>
    </Sheet>
  );
}
